import {z} from 'zod'

import {
    type AnalyticsScenarioParams,
    appendAnalyticsScenarioParams,
} from 'src/lib/analytics-scenario'

import {apiFetch} from '../fetch'
import {
    AssignedUnassignedDataSchema,
    CapacityEngagementMatrixDataSchema,
    GiftCommitmentsDataSchema,
    ProspectDistributionDataSchema,
    ProspectsByGivingLevelDataSchema,
    SummaryStatsDataSchema,
} from './models/analytics'
import {DashboardDataSchema} from './models/dashboard'
import {OrganizationDetailSchema, OrganizationListItemSchema} from './models/organization'
import {ProspectDetailSchema, ProspectsResponseSchema} from './models/prospect'

export async function listOrganizations() {
    const data = await apiFetch<unknown>('/client/organizations')
    return z.array(OrganizationListItemSchema).parse(data)
}

export async function getOrganization(orgId: string) {
    const data = await apiFetch<unknown>(`/client/organizations/${orgId}`)
    return OrganizationDetailSchema.parse(data)
}

/** Build a query string with optional manager filters and admin scenario params. */
function buildAnalyticsQuery(
    scenario: AnalyticsScenarioParams | null | undefined,
    extra?: Record<string, string>,
): string {
    const params = new URLSearchParams()
    if (extra) {
        for (const [key, value] of Object.entries(extra)) {
            params.set(key, value)
        }
    }
    if (scenario) {
        appendAnalyticsScenarioParams(params, scenario)
    }
    const query = params.toString()
    return query ? `?${query}` : ''
}

/** @param scenario When set, sends scenario query params (admin-only on the API). */
export async function getOrganizationDashboard(
    orgId: string,
    scenario?: AnalyticsScenarioParams | null,
) {
    const q = buildAnalyticsQuery(scenario ?? null)
    const data = await apiFetch<unknown>(`/client/organizations/${orgId}/dashboard${q}`)
    return DashboardDataSchema.parse(data)
}

export type ProspectFilters = {
    manager?: string
    capacityBuckets?: string[]
    engagementLevels?: string[]
    totalGiftsMin?: number
    totalGiftsMax?: number
    hasPlannedGivingOpportunity?: boolean
    secInsider?: boolean
    foundation?: boolean
    yearsSinceLastGiftMin?: number
    yearsSinceLastGiftMax?: number
}

export type GetProspectsOptions = {
    page?: number
    pageSize?: number
    sortBy?: string
    sortDir?: 'asc' | 'desc'
    filters?: ProspectFilters
}

/** @param scenario When set, sends scenario query params (admin-only on the API). */
export async function getOrganizationProspects(
    orgId: string,
    options: GetProspectsOptions = {},
    scenario?: AnalyticsScenarioParams | null,
) {
    const {page = 1, pageSize = 25, sortBy = 'last_name', sortDir = 'asc', filters = {}} = options

    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('page_size', String(pageSize))
    params.set('sort_by', sortBy)
    params.set('sort_dir', sortDir)

    const {
        manager,
        capacityBuckets,
        engagementLevels,
        totalGiftsMin,
        totalGiftsMax,
        hasPlannedGivingOpportunity,
        secInsider,
        foundation,
        yearsSinceLastGiftMin,
        yearsSinceLastGiftMax,
    } = filters
    if (manager != null) params.set('manager', manager)
    if (capacityBuckets?.length)
        capacityBuckets.forEach((b) => params.append('capacity_buckets', b))
    if (engagementLevels?.length)
        engagementLevels.forEach((l) => params.append('engagement_levels', l))
    if (totalGiftsMin != null) params.set('total_gifts_min', String(totalGiftsMin))
    if (totalGiftsMax != null) params.set('total_gifts_max', String(totalGiftsMax))
    if (hasPlannedGivingOpportunity === true) {
        params.set('has_planned_giving_opportunity', 'true')
    }
    if (secInsider === true) {
        params.set('sec_insider', 'true')
    }
    if (foundation === true) {
        params.set('foundation', 'true')
    }
    if (yearsSinceLastGiftMin != null) {
        params.set('years_since_last_gift_min', String(yearsSinceLastGiftMin))
    }
    if (yearsSinceLastGiftMax != null) {
        params.set('years_since_last_gift_max', String(yearsSinceLastGiftMax))
    }
    if (scenario) {
        appendAnalyticsScenarioParams(params, scenario)
    }

    const query = params.toString()
    const data = await apiFetch<unknown>(
        `/client/organizations/${orgId}/prospects${query ? `?${query}` : ''}`,
    )
    return ProspectsResponseSchema.parse(data)
}

/** @param scenario When set, sends scenario query params (admin-only on the API). */
export async function getGapAnalysis(orgId: string, scenario?: AnalyticsScenarioParams | null) {
    const q = buildAnalyticsQuery(scenario ?? null)
    const data = await apiFetch<unknown>(`/client/organizations/${orgId}/gap-analysis${q}`)
    return GiftCommitmentsDataSchema.parse(data)
}

export async function getProspectsByCapacity(orgId: string, managerName?: string) {
    const q = managerName ? `?manager_name=${encodeURIComponent(managerName)}` : ''
    const data = await apiFetch<unknown>(`/client/organizations/${orgId}/prospects-by-capacity${q}`)
    return ProspectDistributionDataSchema.parse(data)
}

/** @param scenario When set, sends scenario query params (admin-only on the API). */
export async function getProspectsByEngagement(
    orgId: string,
    managerName?: string,
    scenario?: AnalyticsScenarioParams | null,
) {
    const extra = managerName ? {manager_name: managerName} : undefined
    const q = buildAnalyticsQuery(scenario ?? null, extra)
    const data = await apiFetch<unknown>(
        `/client/organizations/${orgId}/prospects-by-engagement${q}`,
    )
    return CapacityEngagementMatrixDataSchema.parse(data)
}

/** @param scenario When set, sends scenario query params (admin-only on the API). */
export async function getConversionByEngagementLevel(
    orgId: string,
    managerName?: string,
    scenario?: AnalyticsScenarioParams | null,
) {
    const extra = managerName ? {manager_name: managerName} : undefined
    const q = buildAnalyticsQuery(scenario ?? null, extra)
    const data = await apiFetch<unknown>(
        `/client/organizations/${orgId}/conversion-by-engagement-level${q}`,
    )
    return SummaryStatsDataSchema.parse(data)
}

/** @param scenario When set, sends scenario query params (admin-only on the API). */
export async function getAssignedVsUnassigned(
    orgId: string,
    scenario?: AnalyticsScenarioParams | null,
) {
    const q = buildAnalyticsQuery(scenario ?? null)
    const data = await apiFetch<unknown>(
        `/client/organizations/${orgId}/assigned-vs-unassigned${q}`,
    )
    return AssignedUnassignedDataSchema.parse(data)
}

/** @param scenario When set, sends scenario query params (admin-only on the API). */
export async function getProspectsByGivingLevel(
    orgId: string,
    managerName?: string,
    scenario?: AnalyticsScenarioParams | null,
) {
    const extra = managerName ? {manager_name: managerName} : undefined
    const q = buildAnalyticsQuery(scenario ?? null, extra)
    const data = await apiFetch<unknown>(
        `/client/organizations/${orgId}/prospects-by-giving-level${q}`,
    )
    return ProspectsByGivingLevelDataSchema.parse(data)
}

/** @param scenario When set, sends scenario query params (admin-only on the API). */
export async function getProspectDetail(
    orgId: string,
    prospectId: string,
    scenario?: AnalyticsScenarioParams | null,
) {
    const q = buildAnalyticsQuery(scenario ?? null)
    const data = await apiFetch<unknown>(
        `/client/organizations/${orgId}/prospects/${prospectId}${q}`,
    )
    return ProspectDetailSchema.parse(data)
}
